import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, MenuItem, Select, SelectChangeEvent, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import VisitRequestStatusList from './VisitRequestStatusList';
import './VisitRequest.css';
import {
  getVisitorsStatusAPI,
  postVisitorIdSecurityCardNumberDamageAPI,
  postVisitorIdSecurityCardNumberDamageCancelAPI,
  postVisitorIdSecurityCardNumberIssueAPI,
  postVisitorIdSecurityCardNumberLoseAPI,
  postVisitorIdSecurityCardNumberLoseCancelAPI,
  postVisitorIdSecurityCardNumberReturnAPI
} from 'apis/visitRequest/visitorsAPI';
import dayjs, { Dayjs } from 'dayjs';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import VisitRequestStatusPopup from './VisitRequestStatusPopup';
import { VISITOR_SECURITY_CARD_STATUS } from '../../constants/constants';
import SnackbarAlert from 'components/SnackbarAlert';
import { useSnackbarStore } from 'store/useSnackbarStore';
import { getBusinessPlacesAPI } from '../../apis/item/itemAPI';
import { useTranslation } from 'react-i18next';

export type VisitRequest = {
  id: number;
  managerName: string;
  businessPlaceName: string;
  visitFrom: string;
  visitTo: string;
  visitStartTime: string;
  visitEndTime: string;
  purpose: string;
};
export type TVisitorDigitalDevices = {
  id: number;
  digitalDeviceName?: string;
  modelName: string;
  serialNumber: number;
  note: string;
};

type VisitorSecurityCardStatus = 'NONE' | 'ASSIGNED' | 'ISSUED' | 'RETURNED' | 'LOST' | 'DAMAGED';

export type Statistics = {
  totalVisitors: number;
  totalVisits: number;
  totalNotReturns: number;
  totalProblems: number;
};
export type Visitor = {
  id: number;
  name: string;
  companyName: string;
  companyPhoneNumber: string;
  isVip: boolean;
  phoneNumber: string;
  birth: string;
  bloodType: string;
  carNumber: string;
  securityCardNumber: string | null;
  securityManageNumber: string | null;
  securityCardStatus: VisitorSecurityCardStatus;
  securityCardDate: string | null;
  visitRequest: VisitRequest;
  digitalDevices?: TVisitorDigitalDevices[];
};

type VisitorData = {
  code: number;
  data: {
    statistics: Statistics;
    visitors: Visitor[];
    unusualVisitors: Visitor[];
  };
  message: string;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{}}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

export default function VisitRequestStatus() {
  const [statistics, setStatistics] = useState<Statistics>();
  const [visitors, setVisitors] = useState<Visitor[]>([]); // 내방 예정자 정보 관련
  const [unusualVisitors, setUnusualVisitors] = useState<Visitor[]>([]); // 방문 이상한자 정보 관련
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs()); // 기본값으로 오늘 날짜 설정
  const [openCalendar, setOpenCalendar] = useState<boolean>(false); // 날짜 클릭하면 열리는 캘린더
  const [selectedSecurityCard, setSelectedSecurityCard] = useState<Visitor | null>(null); // 클릭한 출입보안카드 정보
  const [openSecurityCardPopup, setOpenSecurityCardPopup] = useState<boolean>(false); // 출입보안카드 정보 팝업 여부
  const [tabValue, setTabValue] = useState<number>(0); // 탭 상태
  const [expanded, setExpanded] = useState<boolean>(true); // 펼치기 상태
  const [name, setName] = useState<string>(''); // 검색어-이름
  const [securityCardNumber, setSecurityCardNumber] = useState<string>(''); // 검색어-카드번호
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar); //Alert zustand 상태 관리
  const [businessPlaces, setBusinessPlaces] = useState<{ id: number; name: string }[]>([]);
  const [businessPlaceId, setBusinessPlaceId] = useState(localStorage.getItem('statusBusinessPlaceId') || '');
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();

  // 출입보안카드 정보 팝업 닫는 함수
  const handleCloseSecurityCardPopup = () => {
    setSelectedSecurityCard(null); // 선택된 사용자 초기화
    setOpenSecurityCardPopup(false); // 출입보안카드 정보 팝업 닫기
  };
  // 출입보안카드 정보 팝업 여는 함수
  const handleOpenSecurityCardPopup = (visitor: Visitor) => {
    setSelectedSecurityCard(visitor); // 클릭한 데이터 넘김
    setOpenSecurityCardPopup(true); // 출입보안카드 정보 팝업 열기
  };

  const fetchBusinessPlaces = async () => {
    const { data } = await getBusinessPlacesAPI();
    setBusinessPlaces(data);
  };

  // (selectedDate가 있다면) 내방신청 현황 API 호출
  const fetchVisitorsStatus = async () => {
    if (selectedDate) {
      const dateString = selectedDate.format('YYYY-MM-DD'); // 선택한 날짜 포맷
      const data: VisitorData = await getVisitorsStatusAPI(dateString, businessPlaceId, name, securityCardNumber);

      setStatistics(data.data.statistics);
      setVisitors(data.data.visitors);
      setUnusualVisitors(data.data.unusualVisitors);
    }
  };

  // actionsAPI 객체를 사용하여 API 호출 매핑
  const actionsAPI = {
    ISSUE: postVisitorIdSecurityCardNumberIssueAPI,
    RETURN: postVisitorIdSecurityCardNumberReturnAPI,
    DAMAGE: postVisitorIdSecurityCardNumberDamageAPI,
    LOSE: postVisitorIdSecurityCardNumberLoseAPI,
    LOSE_CANCELED: postVisitorIdSecurityCardNumberLoseCancelAPI,
    DAMAGE_CANCELED: postVisitorIdSecurityCardNumberDamageCancelAPI
  } as const; // as const로 객체를 리터럴로 고정시켜, keyof 연산을 통해 키를 제한할 수 있도록 설정

  type Action = keyof typeof actionsAPI; // 'Action' 타입은 'RETURN' | 'DAMAGE' | 'LOSE'만 허용

  /**
   * 보안카드 상태 변경을 처리하는 함수
   * @param visitorId - 방문자 ID
   * @param securityCardNumber - 보안카드 번호
   * @param action - 처리할 액션 ('RETURN' | 'DAMAGE' | 'LOSE')
   */

  // 출입보안카드 팝업에서 회수처리/파손처리/분실처리/분실취소 클릭했을때 호출되는 함수
  const handleSecurityCardStateChange = async (
    visitorId: number,
    securityCardNumber: string,
    action: Action // Action 타입으로 제한, 잘못된 값 사용 방지
  ) => {
    const apiFunction = actionsAPI[action]; // actionAPIMap에서 액션에 해당하는 API 함수를 가져옴
    if (apiFunction) {
      // action에 해당하는 API 호출
      const data = await apiFunction(visitorId, securityCardNumber, null);
      // API 응답이 성공(200)일때만, 메시지 표시 및 상태 변경 처리
      if (data.status === 200) {
        const actionMessage = {
          ISSUE: '발급',
          RETURN: '회수',
          DAMAGE: '파손',
          LOSE: '분실',
          LOSE_CANCELED: '분실취소',
          DAMAGE_CANCELED: '파손취소'
        };
        showSnackbar(`${actionMessage[action]} 처리되었습니다.`, 'success'); // 처리 결과 메시지 설정
        setOpenSecurityCardPopup(false); // 보안카드 팝업 닫기
        fetchVisitorsStatus(); // 데이터 갱신
      } else {
        return data.data?.message;
      }
    }
  };
  // 탭 변경 핸들러
  const handleTabValueChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  // DatePicker 달력에서 날짜를 선택했을때, 해당하는 날짜로 변경 핸들러
  const handleDateChange = (date: Dayjs | null) => {
    if (date) setSelectedDate(date);
  };
  // 이전/다음/오늘 버튼 핸들러
  const handlePreviousDay = () => setSelectedDate(selectedDate.subtract(1, 'day'));
  const handleNextDay = () => setSelectedDate(selectedDate.add(1, 'day'));
  const handleToday = () => setSelectedDate(dayjs());

  useEffect(() => {
    fetchBusinessPlaces();
  }, []);

  // 선택된 날짜(selectedDate)가 변경될 때마다 데이터 갱신
  useEffect(() => {
    fetchVisitorsStatus();
  }, [selectedDate, businessPlaceId]);

  const handleBusinessPlaceChange = (event: SelectChangeEvent) => {
    const businessPlaceId = event.target.value;
    setBusinessPlaceId(businessPlaceId);
    localStorage.setItem('statusBusinessPlaceId', businessPlaceId);
  };

  const getTodayVisits = () => {
    const visitors1 = visitors.filter((visitor) => visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.ASSIGNED.key);
    const visitors2 = visitors.filter(
      (visitor) =>
        visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.RETURNED.key && (dayjs(visitor.securityCardDate || dayjs()).isBefore(selectedDate, 'day'))
    );
    const visitors3 = visitors.filter((visitor) => visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.ISSUED.key && (dayjs(visitor.securityCardDate || dayjs()).isBefore(selectedDate, 'day')));
    return [...visitors1, ...visitors2, ...visitors3];
  };

  const getCompletedVisits = () => {
    return visitors.filter(
      (visitor) =>
        visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.RETURNED.key
    );
  };

  const getOngoingVisits = () => {
    return visitors.filter(
      (visitor) =>
        visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.ISSUED.key
    );
  };

  const getUnreturnedCards = () => {
    return unusualVisitors;
  };

  const getLostCards = () => {
    return visitors.filter((visitor) => visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.LOST.key);
  };

  const getDamagedCards = () => {
    return visitors.filter((visitor) => visitor.securityCardStatus === VISITOR_SECURITY_CARD_STATUS.DAMAGED.key);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localStorage.getItem('language') || 'ko'}>
      <SnackbarAlert />
      <Box padding={2} display="flex" flexDirection="column" gap={1}>
        {/* 신청자 정보 */}
        <Box borderRadius={1} bgcolor={theme.palette.contentBlock.background} padding={2} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" onClick={handlePreviousDay}>
                  <ArrowBackIosNewIcon />
                </IconButton>
                <Typography variant="h6" fontWeight="bold" fontSize="24px" onClick={() => setOpenCalendar(true)} style={{ cursor: 'pointer' }}>
                  {selectedDate.format('YYYY-MM-DD')}
                </Typography>
                <DatePicker
                  open={openCalendar}
                  onClose={() => setOpenCalendar(false)}
                  value={selectedDate}
                  onChange={handleDateChange}
                  slotProps={{
                    calendarHeader: {
                      format: 'YYYY-MM'
                    },
                    textField: {
                      placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD',
                      sx: {
                        position: 'absolute', // 화면에서 보이지 않도록 함
                        opacity: 0,
                        pointerEvents: 'none', // 클릭 방지
                        width: 0,
                        height: 0
                      }
                    }
                  }}
                />
                <IconButton size="small" onClick={handleNextDay}>
                  <ArrowForwardIosIcon />
                </IconButton>
                {/* 오늘 */}
                <Button
                  variant="outlined"
                  onClick={handleToday}
                  color="inherit"
                  sx={{ backgroundColor: theme.palette.fieldText.background, borderColor: '#888', borderRadius: '4px', fontWeight: 'bold' }}
                >
                  {t('visit_request_status.today')}
                </Button>
              </Box>

              {/* 사업장 */}
              <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                <Typography
                  minWidth={60}
                  maxWidth={100}
                  variant="caption"
                  color={theme.palette.fieldText.title}
                  height={32}
                  lineHeight={2.75}
                  textAlign="right"
                >
                  {t('visit_request_status.business_place_name')}
                </Typography>
                <Select
                  value={businessPlaceId} // 현재 선택된 값
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1, width: '180px', backgroundColor: theme.palette.fieldText.background, borderRadius: '4px' }}
                  onChange={handleBusinessPlaceChange} // 값 변경 시 호출되는 핸들러
                >
                  <MenuItem key={0} value="" sx={{ fontSize: '12px' }}>
                    -
                  </MenuItem>

                  {businessPlaces?.map((bp) => (
                    <MenuItem key={bp.id} value={bp.id} sx={{ fontSize: '12px' }}>
                      {bp.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                {/* 이름 */}
                <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                  <Typography
                    minWidth={60}
                    maxWidth={100}
                    variant="caption"
                    color={theme.palette.fieldText.title}
                    height={32}
                    lineHeight={2.75}
                    textAlign="right"
                  >
                    {t('visit_request_status.name')}
                  </Typography>
                  <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      e.key === 'Enter' && fetchVisitorsStatus();
                    }}
                    onBlur={fetchVisitorsStatus}
                    sx={{ flexGrow: 1, width: '100px', backgroundColor: theme.palette.fieldText.background, borderRadius: '4px' }}
                    type="text"
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {/* 카드번호 */}
                <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                  <Typography
                    minWidth={60}
                    maxWidth={100}
                    variant="caption"
                    color={theme.palette.fieldText.title}
                    height={32}
                    lineHeight={2.75}
                    textAlign="right"
                  >
                    {t('visit_request_status.card_number')}
                  </Typography>
                  <TextField
                    value={securityCardNumber}
                    onChange={(e) => setSecurityCardNumber(e.target.value)}
                    onKeyDown={(e) => {
                      e.key === 'Enter' && fetchVisitorsStatus();
                    }}
                    onBlur={fetchVisitorsStatus}
                    sx={{ flexGrow: 1, width: '100px', backgroundColor: theme.palette.fieldText.background, borderRadius: '4px' }}
                    type="text"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/*<Button variant="contained" onClick={fetchVisitorsStatus} sx={{ borderRadius: '4px', fontWeight: 'bold' }}>*/}
              {/*  조회*/}
              {/*</Button>*/}
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Box>
                {t('visit_request_status.total_visitors')} <b>{statistics?.totalVisitors || 0}</b> {t('visit_request_status.count')}
              </Box>
              <Box>/</Box>
              <Box>
                {t('visit_request_status.total_visits')} <b>{statistics?.totalVisits || 0}</b> {t('visit_request_status.count')}
              </Box>
              <Box>/</Box>
              <Box>
                {t('visit_request_status.total_not_returns')} <b>{statistics?.totalNotReturns || 0}</b> {t('visit_request_status.count')}
              </Box>
              <Box>/</Box>
              <Box>
                {t('visit_request_status.total_problems')} <b>{statistics?.totalProblems || 0}</b> {t('visit_request_status.count')}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabValueChange} scrollButtons="auto" variant="scrollable">
            {/* 내방예정 / 내방중 */}
            <Tab
              label={`${t('visit_request_status.planned_visit')}(${getTodayVisits()?.length || 0}) / ${t('visit_request_status.visiting')}(${getOngoingVisits()?.length || 0})`}
              {...a11yProps(0)}
            />
            {/* 방문완료 / 카드미회수 */}
            <Tab
              label={`${t('visit_request_status.visit_completed')}(${getCompletedVisits()?.length || 0}) / ${t('visit_request_status.unreturned_card')}(${getUnreturnedCards()?.length || 0})`}
              {...a11yProps(1)}
            />
            {/* 카드분실 / 카드파손 */}
            <Tab
              label={`${t('visit_request_status.lost_card')}(${getLostCards()?.length || 0}) / ${t('visit_request_status.damaged_card')}(${getDamagedCards()?.length || 0})`}
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>
        <CustomTabPanel value={tabValue} index={0}>
          <Box display="flex" flexDirection={{ sm: 'row', xs: 'column' }} gap={2}>
            {/* 내방 예정 */}
            <VisitRequestStatusList
              title={t('visit_request_status.planned_visit')}
              subTitle={t('visit_request_status.planned_visit_subtitle')}
              expanded={expanded}
              visitors={getTodayVisits()}
              handleOpenSecurityCardPopup={handleOpenSecurityCardPopup}
            />
            {/* 내방중 */}
            <VisitRequestStatusList
              title={t('visit_request_status.visiting')}
              subTitle={t('visit_request_status.visiting_subtitle')}
              expanded={expanded}
              visitors={getOngoingVisits()}
              handleOpenSecurityCardPopup={handleOpenSecurityCardPopup}
            />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <Box display="flex" gap={2} flexDirection={{ sm: 'row', xs: 'column' }} flexWrap="wrap">
            {/* 방문 완료 */}
            <VisitRequestStatusList
              title={t('visit_request_status.visit_completed')}
              subTitle={t('visit_request_status.visit_completed_subtitle')}
              expanded={expanded}
              visitors={getCompletedVisits()}
              handleOpenSecurityCardPopup={handleOpenSecurityCardPopup}
            />
            {/* 출입보안카드 미회수 */}
            <VisitRequestStatusList
              title={t('visit_request_status.unreturned_card_1')}
              titleColor={theme.palette.mode == 'dark' ? '#f44336' : '#D32F2F'}
              subTitle={t('visit_request_status.unreturned_card_subtitle')}
              expanded={expanded}
              visitors={getUnreturnedCards()}
              handleOpenSecurityCardPopup={handleOpenSecurityCardPopup}
            />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <Box display="flex" flexDirection={{ sm: 'row', xs: 'column' }} gap={2}>
            {/* 출입보안카드 분실 */}
            <VisitRequestStatusList
              title={t('visit_request_status.lost_card_1')}
              titleColor={theme.palette.mode == 'dark' ? '#f44336' : '#D32F2F'}
              subTitle={t('visit_request_status.lost_card_subtitle')}
              expanded={expanded}
              visitors={getLostCards()}
              handleOpenSecurityCardPopup={handleOpenSecurityCardPopup}
            />
            {/* 출입보안카드 파손 */}
            <VisitRequestStatusList
              title={t('visit_request_status.damaged_card_1')}
              titleColor={theme.palette.mode == 'dark' ? '#f44336' : '#D32F2F'}
              subTitle={t('visit_request_status.damaged_card_subtitle')}
              expanded={expanded}
              visitors={getDamagedCards()}
              handleOpenSecurityCardPopup={handleOpenSecurityCardPopup}
            />
          </Box>
        </CustomTabPanel>
      </Box>
      {/* selectedSecurityCard가 있을때, 팝업 열림 */}
      {selectedSecurityCard && (
        <VisitRequestStatusPopup
          isOpen={openSecurityCardPopup}
          selectedDate={selectedDate}
          handleClose={handleCloseSecurityCardPopup}
          securityCardData={selectedSecurityCard} // 클릭한 보안카드 데이터 전달
          onSecurityCardStateChange={handleSecurityCardStateChange}
        />
      )}
    </LocalizationProvider>
  );
}
